import { useCallback, useEffect, useState, type RefObject } from 'react';
import { useUi } from '../provider/UiContext';
import { useLatest } from './useLatest';

/** Keyboard Lock API (Chrome, Edge): в полном экране Esc приходит странице, выход — удержанием Esc. */
interface IKeyboardLock {
    lock(codes?: string[]): Promise<void>;
    unlock(): void;
}
const keyboardLock = (): IKeyboardLock | undefined => {
    const keyboard = (navigator as Navigator & { keyboard?: Partial<IKeyboardLock> }).keyboard;
    return keyboard?.lock && keyboard.unlock ? (keyboard as IKeyboardLock) : undefined;
};

/**
 * Что переводится в полный экран: элемент или вся страница (`document` — `<html>`: порталы Mantine в `body` — окна,
 * Drawer, уведомления — остаются видны).
 */
export type FullscreenTarget = RefObject<HTMLElement | null> | 'document';

export interface IUseFullscreenOptions {
    /**
     * Отказ браузера — в момент отказа, текстом для пользователя. Без него — уведомление `notify` из `UiProvider`
     * текстом {@link FULLSCREEN_DENIED_TEXT}.
     */
    onError?: (text: string) => void;
    /**
     * Keyboard Lock на Esc, где браузер его умеет: в полном экране Esc остаётся странице (`escapeLocked`), выход —
     * удержание Esc (подсказку показывает браузер) или кнопка. Без Keyboard Lock Esc, как обычно, выводит из полного
     * экрана.
     */
    lockEscape?: boolean;
    /**
     * Браузер отказал или полного экрана нет (iframe без `allow="fullscreen"`) — режим всё равно включается: `fullscreen`
     * — true без системного полного экрана, раскладку на всё окно держат стили по нему; выход — `toggle` или `exit`.
     * Отказ тогда в `onError` не уходит. Esc в режиме выходит из него, если его ещё никто не обработал
     * (`defaultPrevented`) и он не из окна поверх страницы (`aria-modal`), меню или списка, — и в полном экране под
     * `lockEscape`.
     */
    fallback?: boolean;
    /** Размонтирование (уход со страницы) выходит из полного экрана, если в нём наша цель; чужой не трогает. */
    exitOnUnmount?: boolean;
}

export const FULLSCREEN_DENIED_TEXT = 'Браузер не разрешил полноэкранный режим.';

const elementOf = (target: FullscreenTarget): HTMLElement | null =>
    target === 'document' ? document.documentElement : target.current;

/**
 * Экземпляр хука, который ввёл страницу (`document`) в полный экран: цель у всех экземпляров одна, и без хозяина
 * второй на странице считал бы чужой режим своим (и выводил бы из него при размонтировании). Без хозяина (вошли до
 * монтирования или хозяин размонтирован без выхода) режим считается своим у любого экземпляра.
 */
let documentOwner: object | null = null;

const isFullscreen = (target: FullscreenTarget, self: object): boolean => {
    const element = elementOf(target);
    if (!element || document.fullscreenElement !== element) return false;
    return target !== 'document' || documentOwner === null || documentOwner === self;
};

/** Esc здесь закрывает своё: окно поверх страницы, меню, список. */
const ESCAPE_OWNERS = '[aria-modal="true"], [role="menu"], [role="listbox"]';

/**
 * Полный экран элемента или страницы: `fullscreen` — в полном экране именно цель (не другой элемент), `toggle`
 * выходит из любого полного экрана или входит в него целью. Отказ браузера уходит в `onError`, а не в состояние
 * (с `fallback` — включает режим без системного полного экрана).
 */
export function useFullscreen(
    target: FullscreenTarget,
    { onError, lockEscape = false, fallback = false, exitOnUnmount = false }: IUseFullscreenOptions = {},
) {
    const [self] = useState(() => ({}));
    // смонтирован уже в полном экране (переход между разделами, перемонтирование) — сразу верное состояние
    const [fullscreen, setFullscreen] = useState(() => isFullscreen(target, self));
    const [emulated, setEmulated] = useState(false);
    const [escapeLocked, setEscapeLocked] = useState(false);
    const { notify } = useUi();
    const errorRef = useLatest(onError ?? ((text: string) => notify({ message: text, variant: 'red' })));

    useEffect(() => {
        const changed = () => {
            if (!document.fullscreenElement) documentOwner = null;
            const own = isFullscreen(target, self);
            setFullscreen(own);
            // запрос из запасного режима всё же прошёл (повторное нажатие, пока первый был в пути)
            if (own) setEmulated(false);
        };
        // ref элемента заполняется после первой отрисовки: сверка — уже с ним
        changed();
        document.addEventListener('fullscreenchange', changed);
        return () => document.removeEventListener('fullscreenchange', changed);
    }, [target, self]);

    useEffect(() => {
        const lock = lockEscape ? keyboardLock() : undefined;
        if (!fullscreen || !lock) return;
        let active = true;
        lock.lock(['Escape']).then(
            () => active && setEscapeLocked(true),
            () => undefined,
        );
        return () => {
            active = false;
            setEscapeLocked(false);
            lock.unlock();
        };
    }, [fullscreen, lockEscape]);

    // выход браузер может отклонить (документ уже не активен): это не ошибка пользователя
    const exit = useCallback(async () => {
        setEmulated(false);
        if (!document.fullscreenElement) return;
        try {
            await document.exitFullscreen();
        } catch {
            // состояние придёт событием fullscreenchange, если выход всё же случится
        }
    }, []);

    const toggle = useCallback(async () => {
        if (emulated || document.fullscreenElement) return exit();
        if (fallback && document.fullscreenEnabled === false) {
            setEmulated(true);
            return;
        }
        const element = elementOf(target);
        if (!element) return;
        if (target === 'document') documentOwner = self;
        try {
            await element.requestFullscreen();
        } catch {
            if (documentOwner === self && !document.fullscreenElement) documentOwner = null;
            if (!fallback) errorRef.current(FULLSCREEN_DENIED_TEXT);
            else if (!document.fullscreenElement) setEmulated(true);
        }
    }, [target, self, errorRef, emulated, fallback, exit]);

    const active = fullscreen || emulated;

    useEffect(() => {
        if (!fallback || !active) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (event.target instanceof Element && event.target.closest(ESCAPE_OWNERS)) return;
            event.preventDefault();
            void exit();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [fallback, active, exit]);

    // ref элемента к очистке эффектов уже пуст: «наша ли цель» — по последнему состоянию
    const unmountRef = useLatest(exitOnUnmount && fullscreen);
    useEffect(
        () => () => {
            if (documentOwner === self) documentOwner = null;
            if (unmountRef.current && document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
        },
        [unmountRef, self],
    );

    return { fullscreen: active, escapeLocked, toggle, exit };
}
